import React from 'react'
import styled from 'styled-components'
import { line, radius, font, bp, transition, cardStyle, lowerLabel } from 'features/dashboard/tokens'

const LEVELS = [
  { key: 'full',      label: 'Full View',      hint: 'All portfolios' },
  { key: 'portfolio', label: 'Portfolio View', hint: 'Estates & Building Passports' },
  { key: 'estate',    label: 'Estate View',    hint: 'Grouped Building Passports' },
  { key: 'bp',        label: 'Building Passport View', hint: 'One Building Passport' },
  { key: 'unit',      label: 'Unit Passport View', hint: 'Unit Passports & tenancy' },
]

const ACCENT = '#D24724'   // terracotta — the active state colour
const TRACK  = line.base   // muted line / inactive dot

/**
 * Five-dot tracker showing where the user is in the compliance drill path.
 * Dots fill terracotta progressively up to the current level; segments
 * before become clickable "back to" links. The active dot pulses softly.
 *
 * Every level is clickable: backward/current levels call onSelect(level);
 * forward levels (deeper than current) call onJumpToLevel(levelKey) so the
 * caller can open a picker pre-filtered to that level's type.
 *
 * Props:
 *   active: 'full' | 'portfolio' | 'estate' | 'bp' | 'unit'
 *   onSelect: (level) => void
 *   onJumpToLevel: (levelKey) => void  — deeper-than-current levels
 *   reachable: { [level]: bool }  — disables un-reachable levels (e.g. unit when no units)
 */
const ViewTracker = ({ active, onSelect, onJumpToLevel, reachable = {} }) => {
  const activeIdx = LEVELS.findIndex(l => l.key === active)
  const reachedIdx = activeIdx < 0 ? 0 : activeIdx

  return (
    <Wrap role='tablist' aria-label='Compliance view level'>
      {LEVELS.map((lvl, i) => {
        const isActive = i === reachedIdx
        const isReached = i <= reachedIdx
        const isForward = i > reachedIdx
        const reachableLvl = reachable[lvl.key] !== false
        const isClickable = reachableLvl && (isForward ? !!onJumpToLevel : true)
        const hasNext = i < LEVELS.length - 1

        const handleClick = () => {
          if (!isClickable) return
          if (isForward) onJumpToLevel(lvl.key)
          else onSelect(lvl.key)
        }

        return (
          <React.Fragment key={lvl.key}>
            <Segment
              role='tab'
              aria-selected={isActive}
              $reached={isReached}
              $active={isActive}
              $clickable={isClickable}
              $forward={isForward && isClickable}
              onClick={handleClick}
              title={isForward && isClickable ? `Jump to a specific ${lowerLabel(lvl.label.replace(' View', ''))}` : undefined}
            >
              <Label $active={isActive} $forward={isForward && isClickable}>{lvl.label}</Label>
              <Dot $reached={isReached} $active={isActive} $forward={isForward && isClickable}>
                <DotInner $reached={isReached} $active={isActive} />
              </Dot>
              <Hint $active={isActive} $forward={isForward && isClickable}>
                {isForward && isClickable ? `Jump to ${lowerLabel(lvl.hint)}` : lvl.hint}
              </Hint>
            </Segment>
            {hasNext && (
              <Connector $reached={i < reachedIdx} />
            )}
          </React.Fragment>
        )
      })}
    </Wrap>
  )
}

export default ViewTracker

const Wrap = styled.nav`
  ${cardStyle}
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  align-items: center;
  gap: 0;
  padding: 18px 24px;
  margin-bottom: 20px;
  @media (max-width: ${bp.mobile}) { padding: 14px 16px; }
`

const Segment = styled.button`
  font: inherit;
  background: transparent;
  border: none;
  padding: 4px 0;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  position: relative;
  &:hover {
    ${({ $clickable }) => $clickable && `
      transform: translateY(-1px);
    `}
  }
  transition: ${transition};
  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 4px;
    border-radius: ${radius.sm};
  }
`

const Label = styled.span`
  font-size: ${font.body};
  font-weight: ${({ $active }) => $active ? 700 : 600};
  letter-spacing: -0.01em;
  color: ${({ $active, theme }) => $active ? theme.colors.haiti : theme.colors.grey[800]};
  white-space: nowrap;
`

const Dot = styled.span`
  position: relative;
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px;
  border-radius: ${radius.round};
  background: ${({ $reached }) => $reached ? ACCENT : '#fff'};
  border: 2px ${({ $forward }) => $forward ? 'dashed' : 'solid'} ${({ $reached, $forward }) => $reached ? ACCENT : $forward ? ACCENT : TRACK};
  transition: ${transition};

  ${Segment}:hover & {
    ${({ $forward }) => $forward && `background: ${ACCENT}14;`}
  }
`

const DotInner = styled.span`
  width: 6px; height: 6px;
  border-radius: ${radius.round};
  background: ${({ $reached }) => $reached ? '#fff' : TRACK};
`

const Hint = styled.span`
  font-size: ${font.caption};
  font-weight: ${({ $active, $forward }) => ($active || $forward) ? 700 : 500};
  color: ${({ $active, $forward, theme }) => ($active || $forward) ? ACCENT : theme.colors.grey[800]};
  white-space: nowrap;
`

const Connector = styled.span`
  height: 2px;
  background: ${({ $reached }) => $reached ? ACCENT : TRACK};
  transition: ${transition};
  margin-top: 18px;
  align-self: flex-start;
  border-radius: ${radius.xs};
`
